import classNames from "classnames";
import { useInView } from "framer-motion";
import Link from "next/link";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useRef, useState } from "react";

import { useAuthContext } from "~/contexts/auth";
import { useBreakpoint } from "~/contexts/breakpoint";
import { useConfiguration } from "~/contexts/configuration";
import { useServerSideProps } from "~/contexts/server-side-props";
import CrossCountry from "~/types/cross-country";
import Editorials from "~/types/editorials";
import crossCountryUtils from "~/utils/cross-country-utils";
import { assertEditorialType } from "~/utils/editorial-utils";
import powerReviewUtils, { PropsPowerReviewsCultureConfig } from "~/utils/power-reviews-utils";
import productUtils from "~/utils/product-utils";
import textUtils from "~/utils/text-utils";
import TrackingUtils from "~/utils/tracking-utils";

import Pane from "../common/pane";
import ProductActions from "../common/product-actions";
import NotifyMe from "../notify-me/notify-me";
import ProductRatings, { ContextEnum } from "../products/product-ratings";
import PickupStoreWrapper from "../stores/pickup-store-wrapper";
import Breadcrumbs, { Breadcrumb } from "./product-breadcrumbs";
import ProductFixedPane from "./product-fixed-pane";
import ProductGiftAddtobag from "./product-gift-addtobag";
import styles from "./product-info.module.scss";
import ProductMoreInfo from "./product-more-info";
import ProductShadeSelector from "./product-shade-selector";
import ProductStackedPrice from "./product-stacked-price";

enum ProductServices {
  video = "video",
}

// Define an enum for different product services
type Props = {
  root: CrossCountry.DetailedProduct;
  selectedShade: CrossCountry.DetailedProduct;
  shades: CrossCountry.Product[];
};

export default function ProductInfo(props: Props) {
  const t = useTranslations();
  const breakpoint = useBreakpoint();
  const { user, isAuthenticated } = useAuthContext();
  const { serverSideProps } = useServerSideProps();

  const contentUtils = serverSideProps.contentUtils;
  assertEditorialType<Editorials.ContentUtils>(contentUtils, "contentUtils");

  const giftcardBalanceLink = contentUtils.fields.giftcardBalanceLink;

  const _servicesConfigs: Record<ProductServices, Breadcrumb> = {
    [ProductServices.video]: { text: t("generic.services_video_consultation") },
  };

  // Filter services based on environment variable configuration
  const filteredServices = ((process.env.NEXT_PUBLIC_PRODUCT_INFO_SERVICES || "").split(",") as ProductServices[])
    .map((k: ProductServices) => _servicesConfigs[k])
    .filter((item) => !!item);

  const router = useRouter();
  const currentCountry = crossCountryUtils.getCurrentCountryCode(router);
  const { configuration } = useConfiguration();

  const footerRef = useRef<HTMLElement | null>(typeof window !== "undefined" ? document.querySelector("footer") : null);
  // TODO for better handling we should create a custom hook that handles loading
  const footerInView = useInView(footerRef);
  const addToBagRelativePanel = useRef(null);
  const addToBagInView = useInView(addToBagRelativePanel);

  const [showFixedPanel, setShowFixedPanel] = useState(false);
  const [openStores, setOpenStores] = useState(false);
  const [isNotifyMeOpen, setIsNotifyMeOpen] = useState<boolean>(false);
  const [currentStore, setCurrentStore] = useState<EVA.Core.ProductAvailabilityPickupOrganizationUnit>();

  const isGiftCard = productUtils.isGiftCard(props.selectedShade);
  const isVirtual = productUtils.isVirtualProduct(props.selectedShade);

  const URLRef = useRef<string | undefined>(router.asPath);

  // Reset the notify me panel open state when the URL changes
  useEffect(() => {
    if (router.asPath !== undefined && URLRef.current && URLRef.current !== router.asPath) {
      setIsNotifyMeOpen(false);
      URLRef.current = router.asPath;
    }
  }, [setIsNotifyMeOpen, router.asPath]);

  useEffect(() => {
    // no fixed panel when giftcard
    if (!isGiftCard && footerRef.current) {
      if (breakpoint == "desktop") {
        if (addToBagRelativePanel.current) {
          setShowFixedPanel(!addToBagInView && !footerInView);
        }
      } else {
        setShowFixedPanel(!footerInView);
      }
    }
  }, [breakpoint, addToBagInView, footerInView, isGiftCard]);

  const shades = useMemo(() => {
    if (props.shades && props.shades.length > 1) {
      return props.shades;
    }
    if (isVirtual && props.root?.giftCardConfig && props.root.giftCardConfig.fields.virtualCards.length > 1) {
      return props.root.giftCardConfig.fields.virtualCards;
    }
  }, [isVirtual, props.root.giftCardConfig, props.shades]);

  const pwrConfig: PropsPowerReviewsCultureConfig = useMemo(
    () => powerReviewUtils.getPwrConfiguration(configuration, router),
    [configuration, router]
  );

  const productButton = (
    <ProductActions
      hasPrice={productUtils.hasValidDisplayPrice(props.selectedShade)}
      productId={props.selectedShade.product_id}
      outOfStock={!props.selectedShade.has_stock}
      onClickNotifyMe={(value: boolean) => setIsNotifyMeOpen(value)}
      actionsContext="pdp"
      trackingData={TrackingUtils.mapEcomItem(props.root, props.selectedShade, {})}
      maxQuantity={props.selectedShade.ecomm_max_items_per_order}
    />
  );

  return (
    <>
      <div className={styles.pdpInfo}>
        <div className={styles.pdpInfoColumn}>
          <div className={styles.reviews}>
            {process.env.NEXT_PUBLIC_POWERREVIEWS_DISPLAY_DATA_FROM_EVA !== "false" && (
              <ProductRatings average={props.selectedShade.review_average_score || 0} context={ContextEnum.pdp} />
            )}
            {pwrConfig ? (
              <a
                href="#pr-reviewdisplay"
                onClick={(evt) => {
                  evt.preventDefault();
                  evt.stopPropagation();
                  document.getElementById("pr-reviewdisplay")?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                <p className={styles.link}>
                  {t(
                    "generic.reviewsCount",
                    process.env.NEXT_PUBLIC_POWERREVIEWS_DISPLAY_DATA_FROM_EVA !== "false"
                      ? { count: props.selectedShade?.review_total_number || 0 }
                      : undefined
                  )}
                </p>
              </a>
            ) : null}
          </div>
          <div className={styles.productInfoColumn}>
            {((props.shades && props.shades.length > 1) ||
              (isVirtual &&
                props.root?.giftCardConfig &&
                props.root.giftCardConfig.fields.virtualCards.length > 1)) && (
              <Pane>
                <ProductShadeSelector root={props.root} shades={shades!} selectedShade={props.selectedShade} />
              </Pane>
            )}
            {/* Add to cart / Notify Me Form - desktop */}

            <Pane
              ref={addToBagRelativePanel}
              className={classNames(styles.purchaseInfo, isGiftCard ? undefined : styles.hidePane)}
            >
              {!isNotifyMeOpen ? (
                isGiftCard ? (
                  <ProductGiftAddtobag root={props.root} />
                ) : (
                  <>
                    <ProductStackedPrice product={props.selectedShade} />
                    {productButton}
                  </>
                )
              ) : (
                <NotifyMe
                  className={styles.notifyMeForm}
                  productId={props.selectedShade.product_id}
                  onClose={() => setIsNotifyMeOpen(false)}
                />
              )}
            </Pane>
            {/* Check balance for giftcards only */}
            {isGiftCard && (
              <Pane>
                <p className={styles.panelTitle}>
                  <span>{t("generic.giftcard.alreadyhave")}</span>
                </p>
                {giftcardBalanceLink.fields ? (
                  <Link href={textUtils.sanitizeContentfulUrl(giftcardBalanceLink, router)} className={styles.link}>
                    {giftcardBalanceLink.fields.text}
                  </Link>
                ) : null}
              </Pane>
            )}
            {!isGiftCard && !!filteredServices.length && (
              <Pane>
                <p className={styles.panelTitle}>
                  <span>{t("generic.advice")}</span>
                </p>
                <Breadcrumbs breadcrumbs={filteredServices} />
              </Pane>
            )}
            {/* Store availability */}
            {!isVirtual && configuration["App:Ecommerce:EnabledPreferredStores"] === "true" && (
              <Pane>
                <p className={styles.panelTitle}>
                  <span>{t("generic.storesavailabilitytitle")}</span>
                  <button className={styles.link} onClick={() => setOpenStores(true)}>
                    {t("generic.changestore")}
                  </button>
                </p>
                {!currentStore ? (
                  <p className={styles.checkAvailability}>{t("generic.checkAvailabilityNearby")}</p>
                ) : (
                  <p className={styles.infoStore}>{t("generic.checkAvailabilityInStore")}</p>
                )}
                {!!currentStore && (
                  <>
                    <p className={styles.preferredStore}>{currentStore.Name}</p>
                    <div className={styles.rowStock}>
                      <div
                        className={styles.stockIndicator}
                        style={{ backgroundColor: currentStore.HasStock ? "green" : "#CC0C0C" }}
                      />
                      <p className={styles.stockText}>
                        {currentStore.HasStock
                          ? t("generic.productAvailableAtThisLocation")
                          : t("generic.productUnavailableAtThisLocation")}
                      </p>
                    </div>
                  </>
                )}
              </Pane>
            )}
            <PickupStoreWrapper
              inPDP
              inModal
              open={openStores}
              setOpen={setOpenStores}
              products={[
                {
                  ID: props.selectedShade.product_id,
                },
              ]}
              setStore={(store) => {
                // store with availability
                setCurrentStore(store);
              }}
              submitText={t("generic.store.setfavourite")}
            />
          </div>
          {!isGiftCard && (
            <ProductFixedPane
              root={props.root}
              showFixedPanel={showFixedPanel}
              selectedShade={props.selectedShade}
              shades={props.shades}
            />
          )}
          <div className={styles.pdpMoreInfoDesktop}>{<ProductMoreInfo selectedShade={props.selectedShade} />}</div>
        </div>
      </div>
    </>
  );
}
